// src/oura/newscan/fixtures/model.ts
//
// What the fixture store keeps per draft. The 50 are never stored: every read
// plans them again from the business they were drafted from (`built`), the
// words and the staff's edits, the way the backend re-renders from its stored
// variables. Only the edits are state.

import type {
    ScanDraftBusiness,
    ScanDraftCarriedApproval,
    ScanDraftKind,
    ScanDraftPitch,
    ScanDraftPurpose,
    ScanDraftQuestion,
    ScanDraftStatus,
} from "@service/aeo/scanDraftTypes";
import type { RecipeInfo } from "./recipe";
import type { PlannedRow } from "./render";

/** A row's wording as it was when it was locked: word edits and redrafts leave it alone. */
export interface FrozenRow {
    text: string;
    fill: string;
    fill_vars: string[];
    fill_values: Record<string, string>;
}

/** Staff edits to one row, stored by its stable key. */
export interface RowOverride {
    /** Reworded: the staff's own text. */
    text?: string;
    /** The generated wording before the first reword. */
    original?: string;
    locked?: boolean;
    frozen?: FrozenRow;
    /** "Try another", ingredient kind: this row only uses other values (variable -> value). */
    values?: Record<string, string>;
    /** "Try another", template kind: this row uses another template. */
    tpl?: string;
}

export type RecipeMode = "take_all" | "keep_all" | "choose_each";

/**
 * A row the generation-time dedupe pass (dedupe.ts) reworded because its
 * wording repeated another question: moved to another template of its group,
 * or, when no template works, the same sentence with another word for its
 * main variable. Set on generation and on Draft again, never by a staff edit.
 */
export interface AutoReword {
    /** The template the row is asked with now. Absent: the recipe's own template. */
    tpl?: string;
    /** Word fallback: this row only uses another value (variable -> value). */
    values?: Record<string, string>;
    /** The wording it had before, as generated. */
    from: string;
}

export interface JobState {
    seq: number;
    startedAt: number;
    stepMs: number;
    /** Fails once, for the `fail*` sample domains. */
    fail: boolean;
    /** Locked questions a redraft had to let go (their outlet or town went away). */
    unlocked?: number[];
}

/** Everything a write may change, so undo can put it back. */
export interface DraftContent {
    status: ScanDraftStatus;
    business: ScanDraftBusiness;
    built: ScanDraftBusiness;
    round: number;
    wordEdits: Record<string, string>;
    rows: Record<string, RowOverride>;
    /** `resolved`: answered with `record` (kept as it is, nothing changed). */
    answered: Record<string, "dismissed" | "acked" | "resolved">;
    /** Rows still asked with the old recipe's wording. */
    oldKeys: string[];
    recipeMode: RecipeMode | null;
    job: JobState | null;
    /** Decisions answered so far; undo puts the count back with the rest. Older stored drafts lack it. */
    answeredCount?: number;
    /** Rows the dedupe pass reworded, by row key. Older stored drafts lack it. */
    auto?: Record<string, AutoReword>;
    /** Brand drafts: how many times the Google searches were pulled again ("Pull the searches again"). */
    demandRound?: number;
    /** Brand drafts: when the searches were last pulled (ms). */
    demandPulledAt?: number;
    /** first_check: the "For the plan" answers. Absent: none yet. */
    forPlan?: { priorityKeywords: string[] | null; outletNames: string[] | null };
}

/**
 * What a first_check draft was drafted from (JOURNEY-SPEC §2.5): the pitch,
 * the form and the quote, as the onboarding record held them when the form
 * landed. Fixed for the draft's life.
 */
export interface FirstCheckFacts {
    contactName: string;
    answeredAt: string | null;
    formServices: string[];
    formAreas: string[];
    customerQuestions: string[];
    /** The form's goals, as listed. Older stored drafts lack them. */
    goals?: string[];
    /** The form's upcoming plans, as typed. Older stored drafts lack it. */
    plans?: string;
    /** Anything else the client wrote on the form. Older stored drafts lack it. */
    other?: string;
    quote: { ref: string; outlets: number } | null;
    /** Every outlet the plan names (all of a chain's; the draft asks about 9 at most). */
    planOutlets: string[];
    heldKeywords: string[];
    heldOutletNames: string[];
    pitch: ScanDraftPitch | null;
    /** The pitch's services and outlets as it asked them (for the *_missing cards). Empty with no pitch. */
    pitchServices: string[];
    pitchOutlets: string[];
    /** Names the site uses that are not outlets (the backend's outlet check `undeclared_candidates`). Absent: none. */
    siteNames?: string[];
    /** The pitched 50 by row key, when copied (a reusable stamp). Null: not like for like. */
    pitchTexts: Record<string, string> | null;
}

export interface UndoEntry {
    label: string;
    content: DraftContent;
}

export interface DraftState extends DraftContent {
    id: string;
    kind: ScanDraftKind;
    /** Absent or null: the kind's own purpose. */
    purpose?: ScanDraftPurpose | null;
    /** first_check only. */
    firstCheck?: FirstCheckFacts | null;
    /** first_check created approved from the pitch. */
    carriedApproval?: ScanDraftCarriedApproval | null;
    version: number;
    domain: string;
    businessId: string | null;
    sourceAssessmentId: string | null;
    /** The sample this draft draws its words from, by domain. Null: fallback words only. */
    sampleDomain: string | null;
    /** The recipe the set is on before any recipe change. */
    recipeFrom: RecipeInfo;
    /** edit_set / recipe_change: the approved wording by row key. */
    baseline: Record<string, string> | null;
    /** edit_set / recipe_change: when the business's baseline was measured (ms). Absent or null: none. */
    baselineAt?: number | null;
    createdAt: number;
    updatedAt: number;
    approvedAt: number | null;
    startedAssessmentId: string | null;
    /** A started draft's scan finished on the sample clock (ms). Absent: still running, or never started. */
    finishedAt?: number | null;
    /**
     * edit_set / recipe_change started "At the next monthly check" (ms): the
     * set waits for that check and no scan was started. Absent: started now.
     */
    scheduledFor?: number | null;
    /** edit_set / recipe_change: the business's next monthly check (ms). Absent or null: monthly checks are off. */
    monthlyAt?: number | null;
    note: string | null;
    undo: UndoEntry[];
}

/** One of the 50 as assembled for a read. */
export interface AssembledRow {
    plan: PlannedRow;
    question: ScanDraftQuestion;
    /** The wording the row would have without its lock or reword. */
    fresh: string;
    /** The wording under the new recipe's template, no staff edits (recipe change). */
    proposed: string;
    /** The wording under the old recipe, for rows that have one. */
    old: string | null;
    /** Words with no value that this row's wording needs. */
    missing: string[];
}

export const UNDO_LIMIT = 20;

function cloneContent(c: DraftContent): DraftContent {
    return JSON.parse(JSON.stringify(c)) as DraftContent;
}

export function contentOf(state: DraftState): DraftContent {
    return cloneContent({
        status: state.status,
        business: state.business,
        built: state.built,
        round: state.round,
        wordEdits: state.wordEdits,
        rows: state.rows,
        answered: state.answered,
        oldKeys: state.oldKeys,
        recipeMode: state.recipeMode,
        job: state.job,
        answeredCount: state.answeredCount ?? 0,
        auto: state.auto ?? {},
        demandRound: state.demandRound ?? 0,
        demandPulledAt: state.demandPulledAt,
        ...(state.forPlan ? { forPlan: state.forPlan } : {}),
    });
}
